import { BRAND } from '../config/brand';

const NAV = [
  { id:'dashboard',     icon:'🏠', label:'Dashboard'         },
  { id:'search',        icon:'🔍', label:'Search Products'   },
  { id:'bundles',       icon:'✨', label:'AI Bundle Builder' },
  { id:'presentations', icon:'📋', label:'Presentations'     },
  { id:'pos',           icon:'📦', label:'Purchase Orders'   },
  { id:'invoices',      icon:'💳', label:'Invoices'          },
  { id:'quotes',        icon:'📬', label:'Quote Requests'    },
  { id:'clients',       icon:'👤', label:'Clients'           },
  { id:'decorators',    icon:'🧵', label:'Decorators'        },
  { id:'storefront',    icon:'🌐', label:'My Storefront'     },
  { id:'team',          icon:'👥', label:'Team'              },
  // Billing intentionally does NOT appear here. Plan, payment method, and
  // invoices are configuration, not daily work, so they live in
  // Settings → Billing. Ongoing AI bundle usage — the one billing number a
  // rep needs mid-quote — is surfaced on the Dashboard instead.
  { id:'settings',      icon:'⚙️', label:'Settings'          },
];

export default function Sidebar({ screen, navigate, cartCount, newQuoteCount, paidInvoiceCount, dist }) {
  // Admin-only destinations. Team management and account Settings both act on
  // the whole company — inviting people, margins, branding, supplier
  // credentials, billing. A member seeing them meant a rep could open Team and
  // find Invite/Promote/Remove buttons that only 403'd when clicked.
  //
  // Defaults to true when _isAdmin is absent so an older API response or a
  // slow load never hides navigation from a legitimate owner.
  const isAdmin = dist ? dist._isAdmin !== false : true;
  const ADMIN_ONLY = new Set(['team', 'settings']);
  const G = BRAND.colors.primary;
  return (
    <div style={{ width:220, minWidth:220, background:BRAND.colors.dark, display:'flex', flexDirection:'column' }}>
      <div style={{ padding:'20px 20px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
        <div style={{ fontSize:18, fontWeight:600, color:'#fff', letterSpacing:'-0.3px' }}>{BRAND.name}</div>
        <div style={{ fontSize:10, color:G, fontWeight:500, letterSpacing:'0.5px', marginTop:2 }}>BETA</div>
      </div>
      <div style={{ padding:'12px 8px', flex:1 }}>
        {NAV.filter(item => isAdmin || !ADMIN_ONLY.has(item.id)).map(item => {
          // 'clients' nav stays highlighted whether we're on the list ('clients')
          // or a detail page ('client/<id>'). 'settings' stays highlighted for
          // the legacy '#billing' route, which now redirects into
          // Settings → Billing. 'invoices' stays highlighted for invoice detail.
          // All other items match exactly.
          const active = screen === item.id
            || (item.id === 'clients' && screen === 'client')
            || (item.id === 'invoices' && screen === 'invoice')
            || (item.id === 'settings' && screen === 'billing');
          // Badge count per nav item. Note these mean different things:
          // presentations shows the CART count (items staged in the working
          // presentation), quotes shows genuinely NEW inbound requests from
          // the storefront, and invoices shows PAID invoices the distributor
          // hasn't opened yet (see paid_seen on the server) — an unread
          // indicator, cleared by viewing, not by taking action, since
          // there's nothing further to do on money already collected.
          const badgeCount =
            item.id === 'presentations' ? cartCount :
            item.id === 'quotes'        ? newQuoteCount :
            item.id === 'invoices'      ? paidInvoiceCount : 0;
          const badge = badgeCount > 0;
          return (
            <div key={item.id} onClick={() => navigate(item.id)}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px',
                borderRadius:8, cursor:'pointer', marginBottom:1, fontSize:13,
                color: active ? G : '#94A3B8',
                background: active ? `${G}22` : 'transparent',
                fontWeight: active ? 500 : 400, transition:'all 0.15s' }}>
              <span style={{ fontSize:15 }}>{item.icon}</span>
              <span style={{ flex:1 }}>{item.label}</span>
              {badge && <span style={{ background:G, color:'#fff', borderRadius:10, padding:'1px 7px', fontSize:11, fontWeight:600 }}>{badgeCount}</span>}
            </div>
          );
        })}

        {/* Vector art callout — placeholder for future Print Ready Art integration.
            Visually distinct (red) and non-functional for now; clicking does
            nothing. When the integration is ready, swap this onClick to route
            to the vector-art flow. */}
        <div
          onClick={() => { /* placeholder — no nav yet */ }}
          style={{
            display:'flex', alignItems:'center', gap:10,
            padding:'8px 12px', borderRadius:8, marginTop:14,
            border:'1px solid rgba(239,68,68,0.35)',
            background:'rgba(239,68,68,0.08)',
            cursor:'pointer', fontSize:13, fontWeight:600,
            color:'#FCA5A5', letterSpacing:'0.2px',
            transition:'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.16)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
        >
          <span style={{ fontSize:15 }}>🎨</span>
          <span style={{ flex:1 }}>Need Vector Art?</span>
        </div>
      </div>
      {/* No footer here. It previously showed a hardcoded
          "${BRAND.pricing.monthly}/mo · All features included", which was wrong
          for anyone not on a $49 plan, and then briefly duplicated the
          "PromoSearch / BETA" branding already rendered in the header above.
          Real plan and price live in Settings → Billing. */}
    </div>
  );
}
